import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTemplates } from './hooks/useTemplates';
import {
  AddOutlined as AddIcon,
  DeleteOutline as DeleteIcon,
  SettingsOutlined as SettingsIcon,
  UploadFileOutlined as UploadIcon
} from '@mui/icons-material';
import { updateTemplateFile } from './api';

const TemplatesList: React.FC = () => {
  const navigate = useNavigate();
  const { templates, loading, error, removeTemplate, refresh } = useTemplates();

  const handleDelete = (id: number, name: string) => {
    if (window.confirm(`Удалить шаблон "${name}"?`)) {
      removeTemplate(id);
    }
  };

  const handleFileUpdate = async (id: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (window.confirm('Загрузить новую версию файла? Обратите внимание: может потребоваться обновление маппинга.')) {
      try {
        const result = await updateTemplateFile(id, file);
        alert(`Файл успешно обновлен! (Новая версия: ${result.template.version})\nДобавлено новых тегов: ${result.placeholder_diff.added.length}`);
        if (refresh) refresh();
      } catch (e: any) {
        alert(`Ошибка при обновлении файла: ${e.message}`);
      }
    }

    event.target.value = '';
  };

  if (loading) return <div className="p-8 text-gray-500">Загрузка шаблонов...</div>;
  if (error) return <div className="p-8 text-red-500">Ошибка: {error}</div>;

  return (
    <div className="w-full bg-white pb-10">
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8 space-y-6">

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Шаблоны документов</h1>
            <p className="text-sm text-gray-500 mt-1">Управление .docx шаблонами и маппингом данных</p>
          </div>
          <button
            onClick={() => navigate('/documents/templates/new')}
            className="flex items-center gap-2 bg-[#1E61AF] text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors font-medium text-sm"
          >
            <AddIcon fontSize="small" />
            <span>Загрузить шаблон</span>
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500 font-semibold">
                <th className="px-6 py-4">Название</th>
                <th className="px-6 py-4">Тип</th>
                <th className="px-6 py-4">Версия</th>
                <th className="px-6 py-4">Дата обновления</th>
                <th className="px-6 py-4 text-right">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {templates.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    Нет загруженных шаблонов
                  </td>
                </tr>
              ) : (
                templates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">{template.name}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700">
                        {template.document_type?.name || 'Не указан'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">v{template.version}</td>
                    <td className="px-6 py-4 text-gray-600">
                      {new Date(template.updated_at).toLocaleDateString('ru-RU')}
                    </td>
                    <td className="px-6 py-4 text-right space-x-3">

                      <label className="text-gray-400 hover:text-green-600 transition-colors cursor-pointer" title="Загрузить новую версию файла">
                        <UploadIcon fontSize="small" />
                        <input
                          type="file"
                          accept=".docx"
                          className="hidden"
                          onChange={(e) => handleFileUpdate(template.id, e)}
                        />
                      </label>

                      <button
                        onClick={() => navigate(`/documents/templates/${template.id}/mapping`)}
                        className="text-gray-400 hover:text-[#1E61AF] transition-colors"
                        title="Настроить связи полей"
                      >
                        <SettingsIcon fontSize="small" />
                      </button>

                      <button
                        onClick={() => handleDelete(template.id, template.name)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Удалить шаблон"
                      >
                        <DeleteIcon fontSize="small" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TemplatesList;